import { useState } from "react";
import { useLanguage } from "@/lib/language-context";
import { useApp } from "@/lib/app-context";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, Clock, CheckCircle, Package, Phone, FileSearch, XCircle, CreditCard, Building2 } from "lucide-react";
import { visaService, ApplicationStatus } from "@/lib/visa-service";

type StatusType = "pending" | "approved" | "ready" | "rejected" | null;
type SearchMode = "passport" | "application";

export function StatusScreen() {
  const { t } = useLanguage();
  const { setCurrentScreen } = useApp();
  const [searchMode, setSearchMode] = useState<SearchMode>("application");
  const [passportNumber, setPassportNumber] = useState("");
  const [applicationNumber, setApplicationNumber] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [status, setStatus] = useState<StatusType>(null);
  const [backendStatus, setBackendStatus] = useState<ApplicationStatus | null>(null);
  const [searched, setSearched] = useState(false);

  const currentValue = searchMode === "passport" ? passportNumber : applicationNumber;

  /**
   * Map backend status to frontend display status
   * 
   * This function translates the detailed backend application statuses
   * into simplified frontend display states for better user experience.
   */
  const mapBackendStatusToDisplay = (backendStatus: ApplicationStatus): StatusType => {
    switch (backendStatus) {
      // Final status: Visa approved and ready for collection
      case 'approved':
        return 'ready'; // Approved visa is ready for collection
      
      // Initial status: Application created but not yet submitted
      case 'draft':
        return 'pending'; // Application started (draft) - user is still filling out the form
      
      // Submitted status: Application submitted, awaiting payment
      case 'submitted':
        return 'pending'; // Application submitted - waiting for payment confirmation
      
      // Payment status: Payment initiated but not yet confirmed
      case 'payment_pending':
        return 'pending'; // Payment pending - awaiting payment confirmation
      
      // Payment confirmed: Payment received, application queued for processing
      case 'payment_confirmed':
        return 'pending'; // Payment confirmed - application queued for processing
      
      // Processing status: Application is being reviewed internally
      case 'processing':
        return 'pending'; // Application processing - under internal review
      
      // Embassy status: Application forwarded to embassy for final decision
      case 'embassy_submitted':
        return 'pending'; // Submitted to embassy - awaiting embassy decision
      
      // Rejected status: Application was rejected
      case 'rejected':
        return 'rejected'; // Application rejected - show rejection status
      
      // Fallback: Unknown status defaults to pending
      default:
        return 'pending';
    }
  };

  const handleSearch = async () => {
    if (!currentValue.trim()) return;
    
    setIsSearching(true);
    setSearched(false);
    setStatus(null);
    setBackendStatus(null);
    
    try {
      if (searchMode === "application") {
        // Search by application number using the API
        const statusData = await visaService.checkStatus(currentValue.trim().toUpperCase());
        const displayStatus = mapBackendStatusToDisplay(statusData.status);
        setStatus(displayStatus);
        setBackendStatus(statusData.status);
      } else {
        // Passport number search - not yet implemented in backend
        // For now, show a message that passport search is not available
        // TODO: Implement passport number search in backend
        setStatus(null);
        setBackendStatus(null);
      }
    } catch (error) {
      // Application not found or error
      console.error('Error checking status:', error);
      setStatus(null);
      setBackendStatus(null);
    } finally {
      setSearched(true);
      setIsSearching(false);
    }
  };

  /**
   * Get detailed, actionable status message based on backend status
   */
  const getStatusMessage = (backendStatus: ApplicationStatus | null): string => {
    if (!backendStatus) return t.pendingDesc || "Processing...";
    
    switch (backendStatus) {
      case 'draft':
        return "Your application has been started but not yet submitted. Please complete all required steps and submit your application.";
      
      case 'submitted':
        return "Your application has been submitted successfully. Please proceed with payment to continue processing.";
      
      case 'payment_pending':
        return "Payment is pending. Please complete your payment to proceed with visa processing. Contact us if you need assistance with payment.";
      
      case 'payment_confirmed':
        return "Payment received and confirmed. Your application is now queued for processing. We will review your documents shortly.";
      
      case 'processing':
        return "Your application is currently being processed. Our team is reviewing your documents. You will be notified once processing is complete.";
      
      case 'embassy_submitted':
        return "Your application has been submitted to the embassy for final review. This may take some time. We will update you as soon as we receive a decision.";
      
      case 'approved':
        return "Congratulations! Your visa has been approved and is ready for collection. Please visit our office to collect your visa.";
      
      case 'rejected':
        return "Application status unavailable. Please contact our support team for assistance.";
      
      default:
        return t.pendingDesc || "Your application is being processed.";
    }
  };

  const getStatusConfig = (statusType: StatusType) => {
    switch (statusType) {
      case "pending":
        return {
          icon: Clock,
          label: t.statusPending,
          description: getStatusMessage(backendStatus),
          color: "text-warning",
          bgColor: "bg-warning/10",
          borderColor: "border-warning",
        };
      case "approved":
        return {
          icon: CheckCircle,
          label: t.statusApproved,
          description: t.approvedDesc,
          color: "text-[#28A745]",
          bgColor: "bg-[#28A745]/10",
          borderColor: "border-[#28A745]",
        };
      case "ready":
        return {
          icon: Package,
          label: t.statusReady,
          description: getStatusMessage(backendStatus),
          color: "text-primary",
          bgColor: "bg-primary/10",
          borderColor: "border-primary",
        };
      case "rejected":
        return {
          icon: XCircle,
          label: "Rejected",
          description: getStatusMessage(backendStatus),
          color: "text-destructive",
          bgColor: "bg-destructive/10",
          borderColor: "border-destructive",
        };
      default:
        return null;
    }
  };

  /**
   * Get progress step configuration based on backend status
   */
  type ProgressStep = {
    id: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    completed: boolean;
    active: boolean;
    status: 'active' | 'completed' | 'pending';
    isRejected?: boolean;
  };

  const getProgressSteps = (status: ApplicationStatus | null): ProgressStep[] => {
    if (!status) return [];

    const steps: ProgressStep[] = [
      {
        id: 'received',
        label: 'Received',
        icon: CheckCircle,
        completed: ['submitted', 'payment_pending', 'payment_confirmed', 'processing', 'embassy_submitted', 'approved', 'rejected'].includes(status),
        active: status === 'submitted',
        status: status === 'submitted' ? 'active' : (['payment_pending', 'payment_confirmed', 'processing', 'embassy_submitted', 'approved', 'rejected'].includes(status) ? 'completed' : 'pending')
      },
      {
        id: 'payment',
        label: 'Payment',
        icon: CreditCard,
        completed: ['payment_confirmed', 'processing', 'embassy_submitted', 'approved', 'rejected'].includes(status),
        active: ['payment_pending', 'payment_confirmed'].includes(status),
        status: status === 'payment_pending' ? 'active' : (['payment_confirmed', 'processing', 'embassy_submitted', 'approved', 'rejected'].includes(status) ? 'completed' : 'pending')
      },
      {
        id: 'processing',
        label: 'Processing',
        icon: Clock,
        completed: ['embassy_submitted', 'approved', 'rejected'].includes(status),
        active: ['payment_confirmed', 'processing', 'embassy_submitted'].includes(status),
        status: ['payment_confirmed', 'processing', 'embassy_submitted'].includes(status) ? 'active' : (['approved', 'rejected'].includes(status) ? 'completed' : 'pending')
      },
      {
        id: 'embassy',
        label: 'Embassy Review',
        icon: Building2,
        completed: ['approved', 'rejected'].includes(status),
        active: status === 'embassy_submitted',
        status: status === 'embassy_submitted' ? 'active' : (['approved', 'rejected'].includes(status) ? 'completed' : 'pending')
      },
      {
        id: 'final',
        label: status === 'approved' ? 'Ready' : status === 'rejected' ? 'Rejected' : 'Complete',
        icon: status === 'approved' ? Package : status === 'rejected' ? XCircle : CheckCircle,
        completed: ['approved', 'rejected'].includes(status),
        active: ['approved', 'rejected'].includes(status),
        status: ['approved', 'rejected'].includes(status) ? (status === 'approved' ? 'completed' : 'active') : 'pending',
        isRejected: status === 'rejected'
      }
    ];

    // For draft status, show only the first step
    if (status === 'draft') {
      return [{
        id: 'draft',
        label: 'Draft',
        icon: Clock,
        completed: false,
        active: true,
        status: 'active' as const
      }];
    }

    return steps;
  };

  const statusConfig = status ? getStatusConfig(status) : null;

  return (
    <div className="flex flex-col min-h-[calc(100vh-80px)]">
      {/* Main Content */}
      <div className="flex-1 px-4 py-8">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
              <FileSearch className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">{t.statusTitle}</h1>
            <p className="text-muted-foreground">{t.statusDesc}</p>
          </div>

          {/* Search Input */}
          <Card className="p-6 mb-6">
            {/* Primary: Passport Number */}
            {searchMode === "passport" && (
              <>
                <label htmlFor="passport-number" className="block text-lg font-bold text-foreground mb-3">
                  {t.enterPassportNumber}
                </label>
                <div className="flex gap-3 mb-4">
                  <Input
                    id="passport-number"
                    type="text"
                    value={passportNumber}
                    onChange={(e) => setPassportNumber(e.target.value.toUpperCase())}
                    placeholder={t.passportNumberPlaceholder}
                    className="h-14 text-lg font-mono"
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <Button
                    onClick={handleSearch}
                    disabled={!passportNumber.trim() || isSearching}
                    size="lg"
                    className="h-14 w-14 p-0 flex-shrink-0"
                    aria-label={t.checkStatus}
                  >
                    <Search className="h-6 w-6" />
                  </Button>
                </div>
                <button
                  onClick={() => setSearchMode("application")}
                  className="text-sm text-primary underline hover:no-underline"
                >
                  {t.orUseAppNumber}
                </button>
              </>
            )}

            {/* Secondary: Application Number */}
            {searchMode === "application" && (
              <>
                <label htmlFor="app-number" className="block text-lg font-bold text-foreground mb-3">
                  {t.enterAppNumber}
                </label>
                <div className="flex gap-3 mb-4">
                  <Input
                    id="app-number"
                    type="text"
                    value={applicationNumber}
                    onChange={(e) => setApplicationNumber(e.target.value.toUpperCase())}
                    placeholder={t.appNumberPlaceholder}
                    className="h-14 text-lg font-mono"
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <Button
                    onClick={handleSearch}
                    disabled={!applicationNumber.trim() || isSearching}
                    size="lg"
                    className="h-14 w-14 p-0 flex-shrink-0"
                    aria-label={t.checkStatus}
                  >
                    <Search className="h-6 w-6" />
                  </Button>
                </div>
                {/* TODO: Implement passport number search in backend */}
                {/* <button
                  onClick={() => setSearchMode("passport")}
                  className="text-sm text-primary underline hover:no-underline"
                >
                  {t.enterPassportNumber}
                </button> */}
              </>
            )}
          </Card>

          {/* Loading State */}
          {isSearching && (
            <div className="text-center py-8">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">{t.loading}</p>
            </div>
          )}

          {/* Status Result */}
          {searched && !isSearching && statusConfig && (
            <Card className={`p-6 border-2 ${statusConfig.borderColor}`}>
              <div className="text-center">
                <div className={`w-16 h-16 rounded-full ${statusConfig.bgColor} flex items-center justify-center mx-auto mb-4`}>
                  <statusConfig.icon className={`h-8 w-8 ${statusConfig.color}`} />
                </div>
                <p className="text-sm text-muted-foreground mb-1">
                  {searchMode === "passport" ? t.enterPassportNumber : t.applicationNumber}
                </p>
                <p className="text-lg font-mono font-bold text-foreground mb-4">{currentValue}</p>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${statusConfig.bgColor}`}>
                  <span className={`font-bold ${statusConfig.color}`}>{statusConfig.label}</span>
                </div>
                <p className="text-muted-foreground mt-4">{statusConfig.description}</p>
              </div>

              {/* Progress Steps */}
              {backendStatus && (
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex items-center justify-between overflow-x-auto pb-2">
                    {getProgressSteps(backendStatus).map((step, index, steps) => {
                      const isRejected = step.isRejected || false;
                      return (
                        <div key={step.id} className="flex items-center flex-1 min-w-0">
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                              step.status === 'completed' 
                                ? 'bg-[#28A745]' 
                                : step.status === 'active' 
                                ? isRejected
                                  ? 'bg-destructive' 
                                  : 'bg-warning'
                                : 'bg-muted'
                            }`}>
                              <step.icon className={`h-5 w-5 ${
                                step.status === 'pending' 
                                  ? 'text-muted-foreground' 
                                  : 'text-white'
                              }`} />
                            </div>
                            <span className={`text-xs mt-2 text-center max-w-[60px] ${
                              step.status === 'active' || step.status === 'completed'
                                ? 'text-foreground font-medium'
                                : 'text-muted-foreground'
                            }`}>
                              {step.label}
                            </span>
                          </div>
                          {index < steps.length - 1 && (
                            <div className={`flex-1 h-1 mx-1 transition-colors ${
                              step.completed 
                                ? isRejected && index === steps.length - 2
                                  ? 'bg-destructive'
                                  : 'bg-[#28A745]' 
                                : 'bg-muted'
                            }`} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Not Found */}
          {searched && !isSearching && !status && (
            <Card className="p-6 text-center border-destructive">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-destructive" />
              </div>
              <p className="text-lg font-medium text-foreground mb-2">Application Not Found</p>
              <p className="text-muted-foreground mb-4">
                Please check the {searchMode === "passport" ? "passport" : "application"} number and try again.
              </p>
            </Card>
          )}

          {/* Help Card */}
          <Card className="p-4 mt-6 bg-muted border-0">
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-primary flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                {t.contactTitle}:{" "}
                <a href={`tel:+${t.phoneNumberRaw}`} className="text-primary font-bold">
                  {t.phoneNumber}
                </a>
              </p>
            </div>
          </Card>
        </div>
      </div>

      {/* Bottom Nav */}
      <div className="sticky bottom-0 p-4 bg-card border-t border-border safe-area-inset-bottom">
        <div className="max-w-lg mx-auto">
          <Button
            onClick={() => setCurrentScreen("home")}
            variant="outline"
            size="lg"
            className="w-full h-14 text-lg font-bold rounded-xl border-2 bg-transparent"
          >
            {t.goHome}
          </Button>
        </div>
      </div>
    </div>
  );
}
